import { useState, useEffect } from "react";
import HelpButton from "../components/HelpButton";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import WhatsAppSendModal from "../components/WhatsAppSendModal";
import { toast } from "sonner";
import { Plus, WhatsappLogo, ShoppingCart, FilePdf } from "@phosphor-icons/react";
import { buildPOVars } from "../utils/whatsappContext";

const PurchaseOrders = ({ user, logout }) => {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [editor, setEditor] = useState(null);
  const [wa, setWa] = useState(null);

  const load = async () => {
    try {
      const [poRes, vRes] = await Promise.all([
        axios.get(`${API}/purchase-orders`),
        axios.get(`${API}/vendors`),
      ]);
      setItems(poRes.data || []);
      setVendors(vRes.data || []);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editor.vendor_id) { toast.error("Pick a vendor"); return; }
    const vendor = vendors.find(v => v.vendor_id === editor.vendor_id);
    try {
      await axios.post(`${API}/purchase-orders?requester_role=${user?.role || ""}&requester_user_id=${user?.user_id || ""}&requester_name=${encodeURIComponent(user?.name || "")}`, {
        ...editor, vendor_name: vendor?.name || "",
      });
      toast.success("PO created");
      setEditor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const openWa = (po, action) => {
    const vendor = vendors.find(v => v.vendor_id === po.vendor_id) || {};
    setWa({
      module: "purchase",
      receiver: { name: vendor.contact_person || vendor.name || po.vendor_name, phone: vendor.whatsapp_number || vendor.phone, type: "vendor" },
      defaultAction: action,
      context: { variables: buildPOVars(po), related_doc: { type: "purchase-order", id: po.po_id, number: po.po_number } },
    });
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64 p-8">
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">Procurement</div>
            <div className="flex items-center"><h1 className="text-4xl sm:text-5xl tracking-tighter font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Purchase Orders</h1><HelpButton moduleKey="purchase_order" /></div>
            <p className="text-base text-slate-700 mt-1">{items.length} POs · WhatsApp directly to vendors</p>
          </div>
          <button data-testid="po-new-btn" onClick={() => setEditor({ vendor_id: "", total_amount: 0, items: [], notes: "" })} className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium inline-flex items-center gap-2"><Plus size={16} weight="bold" /> New PO</button>
        </div>

        <div data-testid="po-table" className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              {["PO #", "Vendor", "Amount", "Status", "Created", "Actions"].map(h => <th key={h} className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">{h}</th>)}
            </tr></thead>
            <tbody>
              {items.length === 0 ? <tr><td colSpan={6} className="p-12 text-center text-slate-400 text-sm">No POs yet.</td></tr> :
              items.map((po, i) => (
                <tr key={po.po_id} data-testid={`po-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="p-3 font-mono text-sm font-bold text-[#002FA7]">{po.po_number}</td>
                  <td className="p-3 text-sm">{po.vendor_name || "—"}</td>
                  <td className="p-3 text-sm tabular-nums">₹{Number(po.total_amount || 0).toLocaleString("en-IN")}</td>
                  <td className="p-3"><span className="text-[10px] uppercase tracking-wider font-bold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5">{po.status}</span></td>
                  <td className="p-3 text-xs text-slate-500">{(po.created_at || "").slice(0, 10)}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button data-testid={`po-wa-${i}`} onClick={() => openWa(po, "send_po")} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 ml-1 font-bold inline-flex items-center gap-1"><WhatsappLogo size={12} weight="fill" /> Send PO</button>
                    <button data-testid={`po-wa-quote-${i}`} onClick={() => openWa(po, "vendor_quote_request")} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-slate-300 text-slate-700 hover:bg-slate-50 ml-1 font-bold">Quote Req</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editor && (
        <div className="fixed inset-0 z-[150] bg-black/50 flex items-center justify-center p-4" data-testid="po-editor">
          <div className="bg-white border border-slate-200 w-full max-w-xl shadow-2xl">
            <div className="border-b px-5 py-3 flex items-center gap-2"><ShoppingCart size={18} className="text-[#002FA7]" /><h3 className="font-semibold">New Purchase Order</h3></div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Vendor *</label>
                <select data-testid="po-fld-vendor" value={editor.vendor_id} onChange={e => setEditor({...editor, vendor_id: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none">
                  <option value="">— Select —</option>
                  {vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Total Amount (₹)</label>
                <input data-testid="po-fld-amount" type="number" value={editor.total_amount} onChange={e => setEditor({...editor, total_amount: Number(e.target.value)})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Notes</label>
                <textarea value={editor.notes || ""} onChange={e => setEditor({...editor, notes: e.target.value})} rows={3} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
              </div>
            </div>
            <div className="border-t px-5 py-3 flex justify-end gap-2 bg-slate-50">
              <button onClick={() => setEditor(null)} className="rounded-none border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100">Cancel</button>
              <button data-testid="po-save" onClick={save} className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-medium">Save</button>
            </div>
          </div>
        </div>
      )}

      {wa && <WhatsAppSendModal open={true} onClose={() => setWa(null)} {...wa} user={user} />}
    </div>
  );
};

export default PurchaseOrders;
